import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';

import './Medcall2ClientNotesDialog.css';

const AntCloseOutlined = () => (
  <svg aria-hidden="true" fill="currentColor" fillRule="evenodd" focusable="false" viewBox="64 64 896 896">
    <path d="M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.05-.06-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z" />
  </svg>
);

const AntDeleteOutlined = () => (
  <svg aria-hidden="true" fill="currentColor" focusable="false" viewBox="64 64 896 896">
    <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z" />
  </svg>
);

const AntExclamationCircleFilled = () => (
  <svg aria-hidden="true" fill="currentColor" focusable="false" viewBox="64 64 896 896">
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm-32 232c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v272c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V296zm32 440a48.01 48.01 0 010-96 48.01 48.01 0 010 96z" />
  </svg>
);

const AntLoadingOutlined = () => (
  <svg aria-hidden="true" fill="currentColor" focusable="false" viewBox="0 0 1024 1024">
    <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z" />
  </svg>
);

export type Medcall2ClientNote = {
  entryby?: string | null;
  entrydate?: string | null;
  id: number;
  notes?: string | null;
};

export type Medcall2ClientNotesDialogProps = {
  noteDraft: string;
  notes: Medcall2ClientNote[];
  onAddNote: React.FormEventHandler<HTMLFormElement>;
  onClose: () => void;
  onDeleteNote: (noteId: number) => Promise<boolean | void> | boolean | void;
  onNoteDraftChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  open: boolean;
  pending: boolean;
};

export const formatMedcall2ClientNoteDate = (value?: string | null) => {
  const dateMatch = `${value || ''}`.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    return '';
  }

  return `${dateMatch[2]}/${dateMatch[3]}/${dateMatch[1].slice(-2)}`;
};

const resizeNoteEntry = (noteEntry: HTMLTextAreaElement | null) => {
  if (!noteEntry) {
    return;
  }

  const configuredRows = noteEntry.rows;
  noteEntry.rows = 1;
  noteEntry.style.height = 'auto';
  const nextHeight = Math.max(32, noteEntry.scrollHeight);
  noteEntry.rows = configuredRows;
  noteEntry.style.height = `${nextHeight}px`;
};

export const Medcall2ClientNotesDialog = ({
  noteDraft,
  notes,
  onAddNote,
  onClose,
  onDeleteNote,
  onNoteDraftChange,
  open,
  pending,
}: Medcall2ClientNotesDialogProps) => {
  const noteEntryRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [deleteTargetId, setDeleteTargetId] = React.useState<number | null>(null);
  const captureNoteEntry = React.useCallback((noteEntry: HTMLTextAreaElement | null) => {
    noteEntryRef.current = noteEntry;
    resizeNoteEntry(noteEntry);
  }, []);

  React.useLayoutEffect(() => {
    resizeNoteEntry(noteEntryRef.current);
  }, [noteDraft, open]);

  React.useEffect(() => {
    if (open) {
      return;
    }

    setDeleteTargetId(null);
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen || pending) {
      return;
    }

    onClose();
  };

  const handleAddNote = (event: React.FormEvent<HTMLFormElement>) => {
    if (!pending && noteDraft.trim()) {
      onAddNote(event);
      return;
    }

    event.preventDefault();
  };

  const handleDeleteNote = async () => {
    if (deleteTargetId === null || pending) {
      return;
    }

    const deleted = await onDeleteNote(deleteTargetId);
    if (deleted === false) return;
    setDeleteTargetId(null);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent
        aria-describedby={undefined}
        className="medcall2-client-notes-dialog"
        contentHeight="auto"
        contentMaxHeight="calc(100vh - 124px)"
        contentMaxWidth="calc(100vw - 16px)"
        contentTop="100px"
        contentWidth="800px"
        hideCloseButton
        onInteractOutside={() => {
          if (!pending) onClose();
        }}
        onOpenAutoFocus={(event) => event.preventDefault()}
        overlayClassName="medcall2-client-notes-dialog__overlay"
      >
        <form className="medcall2-client-notes" onSubmit={handleAddNote}>
          <DialogHeader className="medcall2-client-notes__header">
            <DialogTitle>Client Notes</DialogTitle>
          </DialogHeader>

          <div className="medcall2-client-notes__body">
            <div className="medcall2-client-notes__stack">
              <Textarea
                className="medcall2-client-notes__entry"
                disabled={pending}
                id="medcall2-client-notes-entry"
                onChange={onNoteDraftChange}
                placeholder="Enter a new note"
                ref={captureNoteEntry}
                rows={2}
                value={noteDraft}
              />

              <div className="medcall2-client-notes__add-row">
                <Button
                  className="medcall2-client-notes__button medcall2-client-notes__button--primary"
                  disabled={pending || !noteDraft.trim()}
                  id="medcall2-client-notes-add"
                  type="submit"
                >
                  {pending ? (
                    <span className="medcall2-client-notes__spinner">
                      <AntLoadingOutlined />
                    </span>
                  ) : null}
                  Add Note
                </Button>
              </div>

              <div className="medcall2-client-notes__divider" role="separator" />

              {notes.length > 0 ? (
                <ol className="medcall2-client-notes__timeline">
                  {notes
                    .filter((note) => Boolean(note.notes?.trim()))
                    .map((note, noteIndex, visibleNotes) => (
                      <li
                        className={`medcall2-client-notes__timeline-item${
                          noteIndex === visibleNotes.length - 1 ? ' is-last' : ''
                        }`}
                        key={note.id}
                      >
                        <div className="medcall2-client-notes__timeline-tail" />
                        <div className="medcall2-client-notes__timeline-head" />
                        <div className="medcall2-client-notes__timeline-label">
                          {formatMedcall2ClientNoteDate(note.entrydate)}
                          {'\u00a0'}
                          <em>({note.entryby || ''})</em>
                        </div>
                        <div className="medcall2-client-notes__timeline-content">
                          <span className="medcall2-client-notes__note-text">{note.notes}</span>
                          <Popover
                            onOpenChange={(nextOpen) => {
                              if (nextOpen) {
                                setDeleteTargetId(note.id);
                                return;
                              }

                              if (deleteTargetId === note.id) {
                                setDeleteTargetId(null);
                              }
                            }}
                            open={deleteTargetId === note.id}
                          >
                            <PopoverTrigger
                              aria-label={`Delete note ${note.id}`}
                              className="medcall2-client-notes__delete-trigger"
                              disabled={pending}
                              id={`medcall2-client-notes-delete-${note.id}`}
                            >
                              <AntDeleteOutlined />
                            </PopoverTrigger>
                            <PopoverContent
                              align="center"
                              className="medcall2-client-notes__delete-confirm"
                              collisionPadding={8}
                              onOpenAutoFocus={(event) => event.preventDefault()}
                              side="top"
                              sideOffset={0}
                            >
                              <div className="medcall2-client-notes__delete-message">
                                <AntExclamationCircleFilled />
                                <span>Delete this note?</span>
                              </div>
                              <div className="medcall2-client-notes__delete-actions">
                                <Button
                                  className="medcall2-client-notes__confirm-button"
                                  id={`medcall2-client-notes-delete-cancel-${note.id}`}
                                  onClick={() => setDeleteTargetId(null)}
                                  type="button"
                                  variant="outline"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  className="medcall2-client-notes__confirm-button medcall2-client-notes__confirm-button--danger"
                                  disabled={pending}
                                  id={`medcall2-client-notes-delete-confirm-${note.id}`}
                                  onClick={handleDeleteNote}
                                  type="button"
                                  variant="destructive"
                                >
                                  Delete
                                </Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </li>
                    ))}
                </ol>
              ) : (
                <span>No notes recorded for this client</span>
              )}
            </div>
          </div>

          <DialogFooter className="medcall2-client-notes__footer">
            <Button
              className="medcall2-client-notes__button medcall2-client-notes__button--primary"
              disabled={pending}
              id="medcall2-client-notes-close"
              onClick={onClose}
              type="button"
            >
              Close
            </Button>
          </DialogFooter>
        </form>
        <DialogPrimitive.Close asChild>
          <button
            aria-label="Close"
            disabled={pending}
            className="medcall2-client-notes__title-close"
            id="medcall2-client-notes-title-close"
            type="button"
          >
            <AntCloseOutlined />
          </button>
        </DialogPrimitive.Close>
      </DialogContent>
    </Dialog>
  );
};
